import React from "react";
import PropTypes from "prop-types";
import { InlineIcon } from "@iconify/react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import {
  ButtonHOC,
  DefaultBtn,
  DefaultMSFNavBtn,
  DefaultSearchBtn,
} from "../HOCs";
import {
  commonStylesBasic,
  commonStylesIconBasic,
  commonStylesPlaceholderBasic,
  commonStylesPlaceholderMSFNavBtn,
  commonStylesPlaceholderSearchBtn,
  getCommonStylesLabelBasic,
} from "../helpers";
import jssVariables from "../../../../jss_setup";

const commonStyles = {
  ...commonStylesBasic,
  padding: jssVariables.normal_gap,
  borderRadius: 0,
};

const ButtonStyles = createUseStyles({
  placeholder: (props) => ({
    ...commonStylesPlaceholderBasic,
    borderRadius: commonStyles.borderRadius,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  ButtonDefault: (props) => ({
    ...commonStyles,
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    background: !props.empty ? jssVariables.colors.base_color : "none",
    color: "white",
    borderRadius: 0,
    padding: [jssVariables.half_gap, jssVariables.double_gap],
    border: props.empty
      ? "1px solid white"
      : `1px solid ${jssVariables.colors.base_color}`,
    "&:hover": {
      color: jssVariables.colors.base_color,
      background: "white",
    },
    ..._.get(props.customStyles, "ButtonDefault", {}),
  }),
  ButtonReverted: (props) => ({
    ...commonStyles,
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    background: !props.empty ? "white" : "none",
    color: jssVariables.colors.base_color,
    padding: jssVariables.half_gap,
    border: `1px solid ${jssVariables.colors.base_color}`,
    "&:hover": {
      color: "white",
      background: jssVariables.colors.base_color,
    },
    ..._.get(props.customStyles, "ButtonReverted", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    fontSize: "medium",
    fontWeight: "bold",
    ..._.get(props.customStyles, "label", {}),
  }),
  icon: (props) => ({
    ...commonStylesIconBasic,
    ..._.get(props.customStyles, "icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    ButtonDefault: (props) => ({
      background: !props.empty ? jssVariables.colors.base_color : "none",
      color: "white",
      border: props.empty
        ? "1px solid white"
        : `1px solid ${jssVariables.colors.base_color}`,
      ..._.get(props.customStyles, "ButtonDefault.smallscreen", {}),
    }),
    ButtonReverted: (props) => ({
      ..._.get(props.customStyles, "ButtonReverted.smallscreen", {}),
    }),
  },
});
var Button = ({ text, showIcon, ...props }) => {
  return (
    <DefaultBtn {...props}>
      <span className={props.classes.label}>{text}</span>
      {showIcon && (
        <InlineIcon
          icon={props.icon ? props.icon : "fa:angle-right"}
          className={props.classes.icon}
        />
      )}
    </DefaultBtn>
  );
};
Button.defaultProps = {
  text: "",
  showIcon: true,
  classes: {},
};
Button.propTypes = {
  text: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  showIcon: PropTypes.bool,
};
Button = ButtonHOC(Button, ButtonStyles);

// =========================== SearchButton ==========================
const SearchButtonStyles = createUseStyles({
  placeholder: (props) => ({
    ...commonStylesPlaceholderSearchBtn,
    borderRadius: jssVariables.half_gap,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  SearchButton: (props) => ({
    ...commonStyles,
    border: `2px solid ${jssVariables.colors.base_color}`,
    background: jssVariables.colors.base_color,
    borderRadius: 0,
    padding: 0,
    color: "white",
    height: "2.5rem",
    fontSize: "large",
    minWidth: "7rem",
    "&:hover": {
      background: "white",
      color: jssVariables.colors.base_color,
    },
    ..._.get(props.customStyles, "SearchButton", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    fontWeight: "bold",
    ..._.get(props.customStyles, "label", {}),
  }),
  icon: (props) => ({
    fontSize: "x-large",
    marginTop: ".2rem",
    ..._.get(props.customStyles, "icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    SearchButton: (props) => ({
      height: "3rem!important",
      ..._.get(props.customStyles, "SearchButton.smallscreen", {}),
    }),
  },
});
var SearchButton = ({ loading, text, ...props }) => {
  return (
    <DefaultSearchBtn {...props}>
      {loading ? (
        <InlineIcon
          icon="line-md:loading-twotone-loop"
          className={props.classes.icon}
        />
      ) : (
        <span className={props.classes.label}>{text}</span>
      )}
    </DefaultSearchBtn>
  );
};
SearchButton.defaultProps = {
  loading: false,
  text: "",
};
SearchButton.propTypes = {
  loading: PropTypes.bool,
  text: PropTypes.string.isRequired,
};
SearchButton = ButtonHOC(SearchButton, SearchButtonStyles);

const MSFNavBtnStyles = createUseStyles({
  placeholder: (props) => ({
    ...commonStylesPlaceholderMSFNavBtn,
    borderRadius: commonStyles.borderRadius,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  button: (props) => ({
    display: "grid",
    gridTemplateColumns: props.showIcon ? "1fr max-content" : "1fr",
    background: "none",
    borderBottom: `2px solid transparent`,
    fontWeight: "normal",
    padding: 0,
    color: jssVariables.colors.text.dark,

    "&:hover:enabled": {
      borderBottom: `2px solid ${jssVariables.colors.base_color}`,
      fontWeight: "bold",
      color: jssVariables.colors.base_color.dark,
    },
    "&:disabled": {
      borderBottom: `2px solid ${jssVariables.colors.base_color}`,
      fontWeight: "bold",
      cursor: "unset",
      color: jssVariables.colors.base_color,
    },
    ..._.get(props.customStyles, "ButtonDefault", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    fontSize: "large",
    textTransform: "uppercase",
    fontWeight: "inherit",
    ..._.get(props.customStyles, "label", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    button: (props) => ({
      ..._.get(props.customStyles, "button.smallscreen", {}),
    }),
  },
});
var MSFNavBtn = ({ text, showIcon, ...props }) => {
  return (
    <DefaultMSFNavBtn {...props}>
      <span className={props.classes.label}>{text}</span>
    </DefaultMSFNavBtn>
  );
};
MSFNavBtn.defaultProps = {
  text: "",
  classes: {},
};
MSFNavBtn.propTypes = {
  text: PropTypes.string.isRequired,
};
MSFNavBtn = ButtonHOC(MSFNavBtn, MSFNavBtnStyles);

export { Button, SearchButton, MSFNavBtn };
